import { useEffect, useMemo, useState } from 'react';
import { studyTypeSections,
         type StudyTypeSectionDto, type StudyTypeSectionTypeOption } from '@/api/endpoints';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';

/**
 * Admin: edycja struktury sekcji per typ opracowania.
 *
 * Każdy typ ma swój uporządkowany spis treści (np. OP_WOD_3B → 11 sekcji wg art. 409 PrW).
 * Admin może: dodawać/usuwać sekcje, zmieniać kolejność strzałkami góra/dół, edytować nazwę,
 * oznaczać `required` (czy sekcja musi mieć ACCEPTED draft), dorzucać podstawę prawną i opis.
 *
 * AiService.dostepneSekcjeForType() czyta z tej tabeli przy budowie spisu treści dla
 * generatora draftów AI.
 */
export function StudyTypeSectionsAdmin() {
  const [types, setTypes] = useState<StudyTypeSectionTypeOption[]>([]);
  const [studyType, setStudyType] = useState<string>('');
  const [rows, setRows] = useState<StudyTypeSectionDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ type: 'ok' | 'err'; text: string } | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<Partial<StudyTypeSectionDto>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSection, setNewSection] = useState({ section: '', legalBasis: '', description: '' });

  const sortedRows = useMemo(() => [...rows].sort((a, b) => a.sortOrder - b.sortOrder), [rows]);

  useEffect(() => {
    studyTypeSections.types()
      .then(t => {
        setTypes(t);
        // Domyślnie wybierz OP_WOD_3B (najczęstszy operat) albo pierwszy z aktywnymi sekcjami
        const defaultType = t.find(x => x.studyType === 'OP_WOD_3B') ?? t.find(x => x.activeCount > 0) ?? t[0];
        if (defaultType && !studyType) setStudyType(defaultType.studyType);
      })
      .catch(e => setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = (st: string) => {
    if (!st) return;
    setLoading(true); setMsg(null);
    studyTypeSections.list(st)
      .then(r => setRows(r))
      .catch(e => setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) }))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (studyType) load(studyType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyType]);

  const moveUp = async (idx: number) => {
    if (idx <= 0) return;
    const newOrder = [...sortedRows];
    [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
    setBusy(true);
    try {
      await studyTypeSections.reorder(studyType, newOrder.map(r => r.id));
      load(studyType);
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const moveDown = async (idx: number) => {
    if (idx >= sortedRows.length - 1) return;
    const newOrder = [...sortedRows];
    [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
    setBusy(true);
    try {
      await studyTypeSections.reorder(studyType, newOrder.map(r => r.id));
      load(studyType);
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (row: StudyTypeSectionDto) => {
    setEditingId(row.id);
    setEditDraft({
      section: row.section,
      required: row.required,
      legalBasis: row.legalBasis,
      description: row.description,
      active: row.active,
    });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft({});
  };

  const saveEdit = async () => {
    if (editingId == null) return;
    setBusy(true);
    try {
      await studyTypeSections.update(editingId, {
        section: editDraft.section,
        required: editDraft.required,
        legalBasis: editDraft.legalBasis,
        description: editDraft.description,
        active: editDraft.active,
      });
      cancelEdit();
      load(studyType);
      setMsg({ type: 'ok', text: 'Zapisano sekcję.' });
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (row: StudyTypeSectionDto) => {
    if (row.active && !confirm(`Wyłączyć sekcję „${row.section}"? Nie będzie pojawiać się w spisie treści generatora AI.`)) return;
    setBusy(true);
    try {
      if (row.active) {
        await studyTypeSections.remove(row.id);
      } else {
        await studyTypeSections.update(row.id, { active: true });
      }
      load(studyType);
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const addSection = async () => {
    if (!newSection.section.trim()) return;
    setBusy(true);
    try {
      await studyTypeSections.create(studyType, {
        section: newSection.section.trim(),
        legalBasis: newSection.legalBasis.trim() || null,
        description: newSection.description.trim() || null,
        required: true,
        active: true,
      });
      setNewSection({ section: '', legalBasis: '', description: '' });
      setShowAddForm(false);
      load(studyType);
      setMsg({ type: 'ok', text: 'Dodano sekcję.' });
    } catch (e) {
      setMsg({ type: 'err', text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1200px] mx-auto">
      <header className="flex items-end justify-between mb-5">
        <div>
          <h1>Sekcje per typ opracowania</h1>
          <div className="text-ink-400 text-[13px]">
            Spis treści dla każdego typu — używany przez generator draftów AI. Kolejność wpływa
            na strukturę operatu, flaga „wymagane" decyduje czy walidator kompletności
            wymaga ACCEPTED draftu.
          </div>
        </div>
      </header>

      {msg && (
        <div className={'mb-4 p-3 rounded-md text-[12px] whitespace-pre-wrap '
          + (msg.type === 'ok' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')}>
          {msg.text}
        </div>
      )}

      <div className="card mb-4">
        <div className="card-body flex items-end gap-3 flex-wrap">
          <label className="field !flex-none w-[400px]">
            <span className="field-label">Typ opracowania</span>
            <SearchableSelect value={studyType} onChange={setStudyType}
                    disabled={busy}>
              {types.map(t => (
                <option key={t.studyType} value={t.studyType}>
                  {t.abbreviation}-{t.code} — {t.name} ({t.activeCount} sekcji)
                </option>
              ))}
            </SearchableSelect>
          </label>
          <button onClick={() => setShowAddForm(s => !s)} disabled={busy || !studyType}
                  className="btn btn-primary btn-sm">
            <Icon name="plus" className="w-3.5 h-3.5"/>
            {showAddForm ? 'Anuluj' : 'Dodaj sekcję'}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="card mb-4">
          <div className="card-head"><div className="card-title">Nowa sekcja</div></div>
          <div className="card-body space-y-3">
            <label className="field">
              <span className="field-label">Nazwa sekcji *</span>
              <input
                value={newSection.section}
                onChange={e => setNewSection(s => ({ ...s, section: e.target.value }))}
                placeholder="np. Charakterystyka wód objętych pozwoleniem"
                className="input"
                disabled={busy}
                autoFocus
              />
            </label>
            <label className="field">
              <span className="field-label">Podstawa prawna (opcjonalnie)</span>
              <input
                value={newSection.legalBasis}
                onChange={e => setNewSection(s => ({ ...s, legalBasis: e.target.value }))}
                placeholder="np. art. 409 ust. 1 pkt 5 PrW"
                className="input"
                disabled={busy}
              />
            </label>
            <label className="field">
              <span className="field-label">Opis (opcjonalnie)</span>
              <textarea
                value={newSection.description}
                onChange={e => setNewSection(s => ({ ...s, description: e.target.value }))}
                rows={3}
                placeholder="Krótki opis czego dotyczy sekcja…"
                className="input"
                disabled={busy}
              />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowAddForm(false); setNewSection({ section: '', legalBasis: '', description: '' }); }}
                      className="btn btn-secondary btn-sm" disabled={busy}>Anuluj</button>
              <button onClick={addSection} className="btn btn-primary btn-sm"
                      disabled={busy || !newSection.section.trim()}>
                <Icon name="check" className="w-3.5 h-3.5"/> Dodaj
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="text-[12px] text-ink-600 mb-4 p-3 bg-ai-500/5 border border-ai-500/20 rounded">
        <div className="font-semibold text-ai-700 mb-0.5">Jak to działa</div>
        Lista sekcji jest pobierana z tej tabeli przez {`AiService.dostepneSekcjeForType()`}.
        Generator draftów (Drafty AI w opracowaniu) i walidator kompletności (panel K10B) używają
        tej samej kolejności. Usuwanie to soft-delete (active=false) — historia opracowań pozostaje.
      </div>

      {loading && <div className="text-ink-400">Ładowanie…</div>}

      {!loading && sortedRows.length === 0 && (
        <div className="card">
          <div className="card-body text-ink-400 text-[13px] italic">
            Brak skonfigurowanych sekcji dla tego typu — dodaj pierwszą używając przycisku „Dodaj sekcję".
          </div>
        </div>
      )}

      {!loading && sortedRows.length > 0 && (
        <div className="space-y-2">
          {sortedRows.map((row, idx) => {
            const editing = editingId === row.id;
            return (
              <div key={row.id}
                   className={'card ' + (!row.active ? 'opacity-50' : '') + ' ' + (editing ? 'border-ai-500/40' : '')}>
                <div className="card-body !p-3">
                  {!editing && (
                    <div className="flex items-start gap-3">
                      <div className="flex flex-col gap-0.5 shrink-0">
                        <button
                          onClick={() => moveUp(idx)}
                          disabled={busy || idx === 0}
                          className="text-ink-400 hover:text-water-600 disabled:opacity-30 p-0.5"
                          title="Przesuń w górę"
                        >
                          <Icon name="chevron" className="w-3 h-3 rotate-180"/>
                        </button>
                        <span className="text-[10px] text-ink-400 font-mono text-center w-6">{row.sortOrder}</span>
                        <button
                          onClick={() => moveDown(idx)}
                          disabled={busy || idx === sortedRows.length - 1}
                          className="text-ink-400 hover:text-water-600 disabled:opacity-30 p-0.5"
                          title="Przesuń w dół"
                        >
                          <Icon name="chevron" className="w-3 h-3"/>
                        </button>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-semibold text-[13px] text-ink-900">{row.section}</div>
                          {row.required
                            ? <span className="pill pill-green text-[10px]">wymagane</span>
                            : <span className="pill text-[10px]">opcjonalne</span>}
                          {!row.active && <span className="pill pill-amber text-[10px]">wyłączone</span>}
                        </div>
                        {row.legalBasis && (
                          <div className="text-[11px] text-ink-500 mt-0.5">📜 {row.legalBasis}</div>
                        )}
                        {row.description && (
                          <div className="text-[11px] text-ink-600 mt-1">{row.description}</div>
                        )}
                      </div>
                      <div className="flex flex-col gap-1 shrink-0">
                        <button onClick={() => startEdit(row)} disabled={busy}
                                className="btn btn-secondary btn-sm">
                          <Icon name="edit" className="w-3 h-3"/> Edytuj
                        </button>
                        <button onClick={() => toggleActive(row)} disabled={busy}
                                className={'btn btn-sm ' + (row.active ? 'btn-secondary' : 'btn-primary')}>
                          {row.active ? 'Wyłącz' : 'Włącz'}
                        </button>
                      </div>
                    </div>
                  )}
                  {editing && (
                    <div className="space-y-2.5">
                      <label className="field">
                        <span className="field-label">Nazwa sekcji</span>
                        <input
                          value={editDraft.section ?? ''}
                          onChange={e => setEditDraft(d => ({ ...d, section: e.target.value }))}
                          className="input"
                          disabled={busy}
                        />
                      </label>
                      <div className="flex gap-3 items-center">
                        <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editDraft.required ?? true}
                            onChange={e => setEditDraft(d => ({ ...d, required: e.target.checked }))}
                            disabled={busy}
                          />
                          Wymagane
                        </label>
                        <label className="flex items-center gap-1.5 text-[12px] cursor-pointer">
                          <input
                            type="checkbox"
                            checked={editDraft.active ?? true}
                            onChange={e => setEditDraft(d => ({ ...d, active: e.target.checked }))}
                            disabled={busy}
                          />
                          Aktywne (w spisie treści generatora)
                        </label>
                      </div>
                      <label className="field">
                        <span className="field-label">Podstawa prawna</span>
                        <input
                          value={editDraft.legalBasis ?? ''}
                          onChange={e => setEditDraft(d => ({ ...d, legalBasis: e.target.value }))}
                          placeholder="np. art. 409 ust. 1 pkt 5 PrW"
                          className="input"
                          disabled={busy}
                        />
                      </label>
                      <label className="field">
                        <span className="field-label">Opis</span>
                        <textarea
                          value={editDraft.description ?? ''}
                          onChange={e => setEditDraft(d => ({ ...d, description: e.target.value }))}
                          rows={2}
                          className="input"
                          disabled={busy}
                        />
                      </label>
                      <div className="flex justify-end gap-2">
                        <button onClick={cancelEdit} className="btn btn-secondary btn-sm" disabled={busy}>
                          Anuluj
                        </button>
                        <button onClick={saveEdit} className="btn btn-primary btn-sm" disabled={busy}>
                          <Icon name="check" className="w-3 h-3"/> Zapisz
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
